import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Modal } from '../../../shared/components/Modal';
import { formatPrice } from '../../../shared/utils/priceUtils';
import {
  useCreateSalesInvoice,
  useDownloadSalesInvoicePdf,
  useRevokeSalesInvoiceToken,
  useSalesInvoices,
  useSendSalesInvoice,
  useSetSalesInvoiceStatus,
} from '../hooks/useSales';
import { SalesInvoiceStatus, type SalesInvoice, type SettableInvoiceStatus } from '../types';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-indigo-100 text-indigo-700',
  SIGNED: 'bg-emerald-100 text-emerald-700',
  PAID: 'bg-emerald-100 text-emerald-700',
  OVERDUE: 'bg-amber-100 text-amber-700',
  CANCELLED: 'bg-muted text-muted-foreground',
  VOID: 'bg-muted text-muted-foreground',
  EXPIRED: 'bg-red-100 text-red-700',
};

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/**
 * Statuses past which the invoice is closed — no send, no link, and no further
 * status change. Cancel/Void are also pointless here.
 */
const TERMINAL: string[] = [
  SalesInvoiceStatus.PAID,
  SalesInvoiceStatus.CANCELLED,
  SalesInvoiceStatus.VOID,
];

/**
 * Statuses that additionally must not be *re-sent*, even though the invoice is
 * still open for bookkeeping. EXPIRED means `priceExpiresAt` has passed, so the
 * quote behind it is stale — emailing that link would put a lapsed price in front
 * of a prospect. Cancel/Void stay available on an expired invoice, which is why
 * this is separate from TERMINAL rather than folded into it.
 */
const NOT_SENDABLE: string[] = [...TERMINAL, SalesInvoiceStatus.EXPIRED];

function SendModal({
  invoice,
  onClose,
}: {
  invoice: SalesInvoice;
  onClose: () => void;
}) {
  const send = useSendSalesInvoice();
  const [email, setEmail] = useState(invoice.recipientEmail ?? '');
  const [name, setName] = useState(invoice.recipientName ?? '');

  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-6">
        <h2 className="text-[18px] font-semibold text-foreground">Send to prospect</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          Emails {invoice.invoiceNumber} with a private link the prospect can view and sign.
          This leaves the building — check the address.
        </p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Recipient email</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Recipient name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" className="h-[40px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="h-[40px]"
            // Sending emails the prospect — must not be double-fireable.
            disabled={!valid || send.isPending}
            loading={send.isPending}
            onClick={async () => {
              await send.mutateAsync({
                id: invoice.id,
                data: { recipientEmail: email.trim(), recipientName: name.trim() || undefined },
              });
              onClose();
            }}
          >
            Send invoice
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateInvoiceModal({
  selectionId,
  onClose,
}: {
  selectionId: string;
  onClose: () => void;
}) {
  const create = useCreateSalesInvoice();
  const [prospectName, setProspectName] = useState('');
  const [prospectCompany, setProspectCompany] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [creditLabel, setCreditLabel] = useState('');
  const [creditNaira, setCreditNaira] = useState(0);

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-6">
        <h2 className="text-[18px] font-semibold text-foreground">Raise invoice</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          Creates a DRAFT from the saved quote. Nothing is sent until you send it.
        </p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Prospect name</span>
            <input
              value={prospectName}
              onChange={(e) => setProspectName(e.target.value)}
              className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Company</span>
            <input
              value={prospectCompany}
              onChange={(e) => setProspectCompany(e.target.value)}
              className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Recipient email</span>
            <input
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={creditEnabled}
              onChange={(e) => setCreditEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            <span className="text-[13px] font-medium text-foreground">Apply a credit / discount</span>
          </label>

          {creditEnabled && (
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[13px] font-medium text-foreground">Credit label</span>
                <input
                  value={creditLabel}
                  onChange={(e) => setCreditLabel(e.target.value)}
                  placeholder="e.g. Launch discount"
                  className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[13px] font-medium text-foreground">Credit amount (₦)</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={creditNaira}
                  onChange={(e) => setCreditNaira(Math.max(0, Number(e.target.value) || 0))}
                  className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
                />
              </label>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" className="h-[40px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="h-[40px]"
            // A second click would raise a duplicate DRAFT from the same selection.
            disabled={prospectName.trim().length === 0 || create.isPending}
            loading={create.isPending}
            onClick={async () => {
              await create.mutateAsync({
                selectionId,
                prospectName: prospectName.trim(),
                prospectCompany: prospectCompany.trim() || undefined,
                recipientEmail: recipientEmail.trim() || undefined,
                creditEnabled,
                ...(creditEnabled
                  ? {
                      creditLabel: creditLabel.trim() || undefined,
                      creditMinor: Math.round(creditNaira * 100),
                    }
                  : {}),
              });
              onClose();
            }}
          >
            Create draft
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function SalesInvoicesPanel({
  canManage,
  pendingSelectionId,
  onConsumeSelection,
}: {
  canManage: boolean;
  /** Set when the calculator just saved a quote, to offer "raise invoice" inline. */
  pendingSelectionId: string | null;
  onConsumeSelection: () => void;
}) {
  const { data: invoices = [], isLoading, error } = useSalesInvoices();
  const setStatus = useSetSalesInvoiceStatus();
  const revoke = useRevokeSalesInvoiceToken();
  const downloadPdf = useDownloadSalesInvoicePdf();

  const [sending, setSending] = useState<SalesInvoice | null>(null);
  const [creatingFrom, setCreatingFrom] = useState<string | null>(null);

  const confirmStatus = (inv: SalesInvoice, status: SettableInvoiceStatus) => {
    const verb = status === 'VOID' ? 'void' : 'cancel';
    if (!window.confirm(`Are you sure you want to ${verb} ${inv.invoiceNumber}? This cannot be undone.`)) return;
    setStatus.mutate({ id: inv.id, status });
  };

  if (isLoading) return <div className="text-center py-16 text-muted-foreground">Loading invoices…</div>;
  if (error) return <div className="text-center py-16 text-red-500">Failed to load sales invoices.</div>;

  return (
    <div>
      {pendingSelectionId && canManage && (
        <div className="mb-4 flex items-center justify-between gap-4 rounded-[12px] border border-emerald-200 bg-emerald-50 px-4 py-3">
          <span className="text-[13px] text-emerald-900">
            A quote was just saved. Raise an invoice from it?
          </span>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" className="h-[36px]" onClick={() => setCreatingFrom(pendingSelectionId)}>
              Raise invoice
            </Button>
            <Button size="sm" variant="outline" className="h-[36px]" onClick={onConsumeSelection}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          No sales invoices yet. Build a quote in the Calculator tab, save it, then raise an invoice.
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Invoice</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Prospect</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Total</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Price expires</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {invoices.map((inv) => {
                const isTerminal = TERMINAL.includes(inv.status);
                // EXPIRED stays cancel/void-able but must not be re-sent — see NOT_SENDABLE.
                const isSendable = !NOT_SENDABLE.includes(inv.status);
                return (
                  <tr key={inv.id} className="hover:bg-muted transition-colors align-middle">
                    <td className="px-6 py-4 text-sm font-medium text-foreground">{inv.invoiceNumber}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {inv.prospectName}
                      {inv.prospectCompany ? ` · ${inv.prospectCompany}` : ''}
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground tabular-nums">
                      {formatPrice(inv.totalPayableMinor)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${
                          STATUS_STYLES[inv.status] ?? 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {fmtDate(inv.priceExpiresAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() =>
                            downloadPdf.mutate({ id: inv.id, invoiceNumber: inv.invoiceNumber })
                          }
                          disabled={downloadPdf.isPending}
                          className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                        >
                          PDF
                        </button>
                        {canManage && !isTerminal && (
                          <>
                            {isSendable && (
                              <button
                                onClick={() => setSending(inv)}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Send
                              </button>
                            )}
                            {isSendable && !inv.accessTokenRevoked && inv.sentToCustomerAt && (
                              <button
                                onClick={() => revoke.mutate(inv.id)}
                                disabled={revoke.isPending}
                                className="text-xs text-amber-700 hover:underline disabled:opacity-50"
                              >
                                Revoke link
                              </button>
                            )}
                            <button
                              onClick={() => confirmStatus(inv, 'CANCELLED')}
                              disabled={setStatus.isPending}
                              className="text-xs text-red-600 hover:underline disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => confirmStatus(inv, 'VOID')}
                              disabled={setStatus.isPending}
                              className="text-xs text-red-600 hover:underline disabled:opacity-50"
                            >
                              Void
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sending && <SendModal invoice={sending} onClose={() => setSending(null)} />}
      {creatingFrom && (
        <CreateInvoiceModal
          selectionId={creatingFrom}
          onClose={() => {
            setCreatingFrom(null);
            onConsumeSelection();
          }}
        />
      )}
    </div>
  );
}
